import {Component, computed, effect, input, OnDestroy, OnInit, signal} from '@angular/core';
import {FileUploadComponent} from '../../file-upload/file-upload.component';
import {DatePipe} from '@angular/common';
import {ReactiveFormsModule} from '@angular/forms';
import {PatientFile} from '../../../models/patient-file/patient-file.model';
import {DocumentTypeModel} from '../../../models/document-types/document-type';
import {QueryModel} from '../../../services/http/http.service';
import {forkJoin, Subject, takeUntil} from 'rxjs';
import {iResponseList} from '../../../models/response/response.model';
import {UploadService} from '../../../services/upload/upload.service';
import {ValueService} from '../../../services/value/value.service';
import {PatientModel} from '../../../models/patient/patient.model';
import {FilePermissionModalComponent} from '../../modals/file-permission-modal/file-permission-modal.component';
import {MatDialog} from '@angular/material/dialog';
import {DoctorModel} from '../../../models/doctor/doctor.model';
import {DoctorService} from '../../../services/doctor/doctor.service';

@Component({
    selector: 'app-patient-file-handling',
    imports: [
        FileUploadComponent,
        ReactiveFormsModule,
        DatePipe
    ],
    templateUrl: './patient-file-handling.component.html',
    styleUrl: './patient-file-handling.component.sass',
    standalone: true
})
export class PatientFileHandlingComponent implements OnInit, OnDestroy {
    patient = input<PatientModel>();
    isDoctor = input<boolean>(false);
    showOnlyType = input<string | null>(null);
    excludeType = input<string | null>(null);
    private destroy$ = new Subject<void>();

    files = signal<PatientFile[]>([]);
    documentTypes = signal<DocumentTypeModel[]>([]);
    doctorList = signal<DoctorModel[]>([]);
    isLoadingFiles = signal<boolean>(false);

    medicationFiles = computed(() => this.files().filter(file => file.documentType.id === 'medication-plan'));
    documentFiles = computed(() => this.files().filter(file => file.documentType.id === 'documents'));
    doctorLetterFiles = computed(() => this.files().filter(file => file.documentType.id === 'doctor-letters'));
    uploadedFiles = computed(() => this.files().filter(file => file.documentType.id === 'uploads' || file.documentType.id === 'other'));
    idFiles = computed(() => this.files().filter(file => file.documentType.id === 'id-documents'));

    openPermissionsId = signal<string>('');

    constructor(
        private uploadService: UploadService,
        private valueService: ValueService,
        private dialog: MatDialog,
        private doctorService: DoctorService,
    ) {
        effect(() => {
            const patient = this.patient();
            if (patient) {
                this.getFiles(patient.id);
            }
        });
    }

    ngOnInit(): void {
        this.getDocumentTypes();
        this.getDoctors();
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }

    openPermissions(fileId: string): void {
        if (this.openPermissionsId() === fileId) {
            this.openPermissionsId.set('');
        } else {
            this.openPermissionsId.set(fileId);
        }
    }

    triggerDelete(fileId: string): void {
        this.uploadService.deleteFile(fileId)
            .pipe(takeUntil(this.destroy$))
            .subscribe({
                next: () => {
                    const patient = this.patient();
                    if (patient) {
                        this.getFiles(patient.id);
                    }
                },
                error: (error) => {
                    console.error(error)
                }
            })
    }

    deletePermission(_id: string): void {
    }

    openPermissionModal(file: PatientFile, fileName: string = ''): void {
        const dialogRef = this.dialog.open(FilePermissionModalComponent, {
            data: {
                file: file,
                fileName: fileName
            }
        });
        dialogRef.afterClosed().subscribe(_result => {
        });
    }

    triggerFileUpload(file: File, documentTypeId: string): void {
        const patient = this.patient();
        let patientId: string | null;
        if (this.isDoctor() && patient) {
            patientId = patient.id;
        } else {
            patientId = null
        }
        this.uploadService.uploadFile(file, documentTypeId, patientId)
            .pipe(takeUntil(this.destroy$))
            .subscribe({
                next: () => {
                    if (patient) {
                        this.getFiles(patient.id);
                    }
                },
                error: (error) => {
                    console.error(error)
                }
            })
    }

    triggerDownload(file: PatientFile): void {
        const patient = this.patient();
        if (this.isDoctor() && patient) {
            this.uploadService.downloadWithQuery(file.id, file.fileName, file.contentType, {userId: patient.id})
        } else {
            this.uploadService.downloadFile(file.id, file.fileName, file.contentType)
        }
    }

    private getDocumentTypes(): void {
        this.valueService.getDocumentTypes()
            .pipe(takeUntil(this.destroy$))
            .subscribe({
                next: (response: iResponseList<DocumentTypeModel>) => {
                    this.documentTypes.set(response.data);
                },
                error: (error) => {
                    console.error(error)
                }
            })
    }

    private getFiles(patientId: string): void {
        this.isLoadingFiles.set(true);
        const query: QueryModel = {
            userId: patientId
        }
        this.uploadService.getFiles(query)
            .pipe(takeUntil(this.destroy$))
            .subscribe({
                next: (response: iResponseList<PatientFile>) => {
                    const files = response.data;
                    this.files.set(files);

                    // Wenn Ausweisdokumente dabei sind, laden wir die Base64-Daten für die Vorschau
                    const hasIdDocs = files.some(f => f.documentType.id === 'id-documents');
                    if (hasIdDocs) {
                        this.getBase64Files(patientId, files);
                    } else {
                        this.isLoadingFiles.set(false);
                    }
                },
                error: (error) => {
                    console.error(error)
                    this.isLoadingFiles.set(false);
                }
            })
    }

    private getBase64Files(patientId: string, files: PatientFile[]): void {
        const idDocs = files.filter(f => f.documentType.id === 'id-documents');
        const queries = idDocs.map(file => {
            const query: QueryModel = {
                userId: patientId,
                base64: true
            };
            return this.uploadService.getFile(file.id, query)
                .pipe(takeUntil(this.destroy$));
        });

        if (queries.length === 0) {
            this.isLoadingFiles.set(false);
            return;
        }

        forkJoin(queries).subscribe({
            next: (responses: any[]) => {
                const updatedFiles = [...this.files()];
                responses.forEach((response, _index) => {
                    // Wir suchen das passende File in der Liste und setzen die base46 Daten
                    // Da forkJoin die Reihenfolge beibehält, nutzen wir idDocs[index].id
                    const fileId = idDocs[_index].id;
                    const fileIndex = updatedFiles.findIndex(f => f.id === fileId);
                    if (fileIndex !== -1) {
                        updatedFiles[fileIndex].base46 = response.data;
                    }
                });
                this.files.set(updatedFiles);
                this.isLoadingFiles.set(false);
            },
            error: (error) => {
                console.error(error);
                this.isLoadingFiles.set(false);
            }
        });
    }

    private getDoctors(): void {
        this.doctorService.getDoctors()
            .pipe(takeUntil(this.destroy$))
            .subscribe({
                next: (response: iResponseList<DoctorModel>) => {
                    this.doctorList.set(response.data);
                },
                error: (error) => {
                    console.error(error)
                }
            })
    }
}
